import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container", "loadingIndicator", "sentinel"];
  static values = {
    offset: Number,
    limit: Number,
    hasMoreResults: Boolean,
    loading: Boolean,
    leagueSeasonId: Number,
  };

  connect() {
    this.setupIntersectionObserver();
    this.setupFrameListener();
  }

  disconnect() {
    this.removeIntersectionObserver();
    this.removeFrameListener();
    this.hideLoadingIndicator(); // Clean up any pending timeout
  }

  setupIntersectionObserver() {
    if (!this.hasSentinelTarget) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            this.hasMoreResultsValue &&
            !this.loadingValue
          ) {
            this.loadMoreTransactions();
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: "500px", // Start loading 500px before sentinel comes into view
        threshold: 0,
      },
    );

    this.observer.observe(this.sentinelTarget);
  }

  removeIntersectionObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  setupFrameListener() {
    this.boundFrameLoadHandler = this.handleFrameLoad.bind(this);
    document.addEventListener("turbo:frame-load", this.boundFrameLoadHandler);
  }

  removeFrameListener() {
    if (this.boundFrameLoadHandler) {
      document.removeEventListener(
        "turbo:frame-load",
        this.boundFrameLoadHandler,
      );
    }
  }

  handleFrameLoad(event) {
    // Handle both initial load and infinite scroll loads
    if (
      event.target.id === "transactions" ||
      event.target.id === "transactions-more"
    ) {
      this.loadingValue = false;
      this.hideLoadingIndicator();
    }
  }

  async loadMoreTransactions() {
    if (this.loadingValue || !this.hasMoreResultsValue) {
      return;
    }

    this.loadingValue = true;
    this.showLoadingIndicator();

    try {
      const nextOffset = this.offsetValue + this.limitValue;
      const url = new URL(window.location);

      // Preserve all existing query parameters (including filter)
      // Then override offset and add partial
      url.searchParams.set("offset", nextOffset);
      url.searchParams.set("partial", "true");

      const response = await fetch(url.toString(), {
        headers: {
          Accept: "text/html",
          "Turbo-Frame": "transactions-more",
        },
      });

      if (response.ok) {
        const html = await response.text();
        this.appendTransactions(html);
        this.offsetValue = nextOffset;
      }
    } catch (error) {
      console.error("Failed to load more transactions:", error);
    } finally {
      this.loadingValue = false;
      this.hideLoadingIndicator();
    }
  }

  appendTransactions(html) {
    // Create a temporary container to parse the HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Find the new transactions and metadata
    const newTransactions = tempDiv.querySelector("[data-transactions]");
    const hasMoreMeta = tempDiv.querySelector("[data-has-more]");

    if (newTransactions && this.hasContainerTarget) {
      // Append new transactions to existing container
      this.containerTarget.appendChild(newTransactions);

      // Update hasMoreResults value
      if (hasMoreMeta) {
        this.hasMoreResultsValue = hasMoreMeta.dataset.hasMore === "true";
      }

      // If no more results, hide the sentinel
      if (!this.hasMoreResultsValue && this.hasSentinelTarget) {
        this.sentinelTarget.classList.add("hidden");
      }
    }
  }

  showLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      // Clear any existing timeout
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
      }

      // Add 500ms delay before showing spinner
      this.loadingTimeout = setTimeout(() => {
        if (this.loadingValue && this.hasLoadingIndicatorTarget) {
          this.loadingIndicatorTarget.classList.remove("invisible");
          this.loadingIndicatorTarget.classList.add("visible");
        }
      }, 500);
    }
  }

  hideLoadingIndicator() {
    // Clear any pending timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove("visible");
      this.loadingIndicatorTarget.classList.add("invisible");
    }
  }

  // Reset to initial state when filter changes
  reset() {
    this.offsetValue = 0;
    this.hasMoreResultsValue = true;
    this.loadingValue = false;
    this.hideLoadingIndicator();

    // Show sentinel again in case it was hidden
    if (this.hasSentinelTarget) {
      this.sentinelTarget.classList.remove("hidden");
    }
  }
}
